<template>
  <div class="dropdown dropdown-input" :class="{ opened: isShow, 'input-group': !findOnlyByThis }">
    <input
      type="text"
      class="form-control"
      v-model="searchString"
      data-testid="input-search"
      :placeholder="placeholder"
      tabindex="0"
      ref="input"
      @keydown.down.prevent="handleInputDown"
    />
    <div class="dropdown-menu dropdown-menu-wide" :class="{ show: isShow }" ref="menu">
      <div v-if="!hasFoundResults" class="text-secondary p-2">
        {{ $t('src/components/filters/personsearchfilter.9374') }}
      </div>
      <template v-else>
        <a
          v-for="(person, index) in foundList"
          :key="person.id"
          class="dropdown-item"
          tabindex="0"
          :data-testid="`search-patient-${person.id}`"
          @click.prevent="selectPerson(person.id, index)"
          @keydown.enter.stop="selectPerson(person.id, index)"
          @keydown.down.prevent="handleItemDown"
          @keydown.up.prevent="handleItemUp"
        >
          {{ getFullNameWithBirth(person) }}
        </a>
      </template>
      <button
        v-if="showAddPatientButton"
        type="button"
        class="btn btn-primary"
        @click="openAddPatientForm"
        style="width: 100%; height: 40px; border-radius: 2px"
      >
        {{ $t('button.addNewPatient') }}
      </button>
    </div>
    <div v-if="!findOnlyByThis" class="input-group-append">
      <base-dropdown-select
        :options="unselectedOptions"
        :title="options[selectedFindBy].title"
        v-model="selectedFindBy"
        class="dropdown-append"
      ></base-dropdown-select>
    </div>
  </div>
</template>

<script>
import http from '@/modules/http.js'
import { stringify } from 'qs'
import { fullName } from '@/mixins/full-name.js'
import { roles, FIND_BY_PERSON } from '@/main.config.js'
import { userRights } from '@/mixins/user-rights.js'

export default {
  name: 'PersonSearchFilter',
  components: {
    BaseDropdownSelect: () => import('@/base/BaseDropdownSelect.vue')
  },
  mixins: [fullName, userRights],
  props: {
    id: {
      type: [Number, String],
      default: null
    },
    findOnlyByThis: {
      type: Boolean,
      default: false
    },
    findBy: {
      type: String,
      default: FIND_BY_PERSON.patient,
      validator: value => [FIND_BY_PERSON.doctor, FIND_BY_PERSON.patient].includes(value)
    },
    showAddPatient: {
      type: Boolean,
      default: false
    },
    /** Исключить текущий элемент из списка для выбора
     * @defaultvalue false
     */
    excludeCurrentId: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      searchString: '',
      isSearching: false,
      foundList: [],
      currentSelection: null,
      isShow: false,
      selectedFindBy: this.findBy,
      options: {
        [FIND_BY_PERSON.patient]: {
          title: this.$t('entity.patients')
        },
        [FIND_BY_PERSON.doctor]: {
          title: this.$t('entity.doctors')
        }
      }
    }
  },
  computed: {
    placeholder() {
      return this.isNeuroAdmin
        ? this.$t('src/components/filters/personsearchfilter.7353')
        : this.$t('src/components/filters/personsearchfilter.7352')
    },
    unselectedOptions() {
      return Object.keys(this.options).reduce((result, key) => {
        if (key !== this.selectedFindBy) {
          result[key] = this.options[key]
        }
        return result
      }, {})
    },
    hasFoundResults() {
      return this.foundList.length > 0
    },
    hospitalId() {
      return this.$store.getters.SELECTED_HOSPITAL
    },
    selectFields() {
      return this.selectedFindBy === FIND_BY_PERSON.patient
        ? ['id', 'surname', 'name', 'middle_name', 'birth_date', 'hospital_id', 'gender']
        : ['user.id', 'surname', 'name', 'middle_name', 'hospital_id']
    },
    searchParams() {
      let params = {
        fullname: { $like: `%${this.searchString}%` },
        $select: this.selectFields
      }

      if (this.selectedFindBy === FIND_BY_PERSON.doctor) {
        params['user_role.title'] = roles.doctor
        if (this.searchString === '') {
          delete params.fullname
        }
      }
      if (this.hospitalId) {
        params.hospital_id = this.hospitalId
      }
      return params
    },
    apiURL() {
      return this.selectedFindBy === FIND_BY_PERSON.patient ? '/patients' : '/users'
    },
    currentFullName() {
      return this.currentSelection ? this.getFullNameWithBirth(this.currentSelection) : ''
    },
    currentShortName() {
      return this.currentSelection ? this.getSurnameWithInitials(this.currentSelection) : ''
    },
    shouldStopSearch() {
      return this.searchString === this.currentFullName || this.searchString.length < 1
    },
    showAddPatientButton() {
      return this.showAddPatient
    }
  },
  watch: {
    id() {
      if (!this.id) {
        this.clearCurrentSelection()
      } else {
        this.fetchCurrentSelectionById()
      }
    },
    searchString() {
      this.$emit('set:searchString', this.searchString)
      if (this.shouldStopSearch) {
        this.isShow = false
        this.clearFoundList()
        if (!this.searchString.length) {
          this.clearSelection()
        }
        return
      }
      this.performSearch()
    },
    selectedFindBy() {
      this.clearFoundList()
      this.clearCurrentSelection()
      this.isShow = false
      this.$refs.input.focus()
    },
    findBy() {
      this.selectedFindBy = this.findBy
    }
  },
  mounted() {
    if (this.id) {
      if (this.excludeCurrentId)
        this.clearCurrentSelection()
      else
        this.fetchCurrentSelectionById()
    }

    this.$root.$on('close-dropdown', () => {
      this.isShow = false
    })
  },
  methods: {
    performSearch() {
      if (this.isSearching) {
        setTimeout(this.performSearch, 400)
        return
      }

      this.isSearching = true

      http
        .get(this.apiURL, {
          params: this.searchParams,
          paramsSerializer: params => stringify(params, { arrayFormat: 'indices', encodeValuesOnly: true })
        })
        .then(response => {
          this.foundList = response.data.data
          if (this.excludeCurrentId) {
            this.foundList = this.foundList.filter(item => item.id != this.id)
          }

          this.isShow = true
        })
        .catch(error => {
          console.error(error)
        })
        .finally(() => {
          this.isSearching = false
        })
    },
    selectPerson(id, index) {
      this.currentSelection = this.foundList[index]
      this.searchString = this.currentFullName
      this.$emit('select:item', {
        id,
        findBy: this.selectedFindBy,
        name: this.currentShortName,
        current: this.currentSelection
      })
      this.isShow = false
    },
    clearFoundList() {
      this.foundList = []
    },
    clearSelection() {
      this.currentSelection = null
      this.$emit('select', { id: null, findBy: this.selectedFindBy, name: null })
    },
    clearCurrentSelection() {
      this.searchString = ''
      this.currentSelection = null
    },
    fetchCurrentSelectionById() {
      http
        .get(`${this.apiURL}/${this.id}`, {
          params: { $select: this.selectFields },
          paramsSerializer: params => stringify(params, { arrayFormat: 'indices', encodeValuesOnly: true })
        })
        .then(response => {
          this.currentSelection = response.data
          this.searchString = this.currentFullName
        })
        .catch(error => {
          console.error(error)
        })
    },
    handleInputDown() {
      if (this.hasFoundResults) {
        this.$refs.menu.children[0].focus()
      }
    },
    handleItemDown(e) {
      if (e.target.nextElementSibling) {
        e.target.nextElementSibling.focus()
      }
    },
    handleItemUp(e) {
      if (e.target.previousElementSibling) {
        e.target.previousElementSibling.focus()
      } else {
        this.$refs.input.focus()
      }
    },
    openAddPatientForm() {
      this.$emit('openAddPatient', this.currentSelection)
    }
  }
}
</script>
